import { Badge, Button, Card, EmptyState, Skeleton, formatDate } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { listAvailability, listPtSessions, type Availability, type PtSession } from "../../api/pt";
import { useServices } from "../../app/services";
import { addDays, minutesOfDay } from "../../lib/dates";
import { AvailabilityDrawer } from "./AvailabilityDrawer";
import k from "./classes.module.css";
import { Failure, Field, styles as c, useIsWide, useScope, useStaffNames } from "./common";
import { PtBookDrawer } from "./PtBookDrawer";
import { LeaveImpact, UnmarkedSessions } from "./PtExtras";
import { PtSessionDrawer, type Prefill } from "./PtSessionDrawer";
import { areaAllows } from "../../app/nav";
import { fromMinutes, layoutLanes, minutesFromOffset, toMinutes, weekdayOf, weekDates } from "./time";

const PX = 1;

/** Availability windows that apply on `date` for one trainer: a dated exception replaces the weekly rule (no windows = off that day). */
export function windowsOn(a: Availability | undefined, date: string): { start: string; end: string }[] {
  if (!a) return [];
  const ex = a.exceptions.find((e) => e.date === date);
  if (ex) return ex.windows ?? [];
  return a.weekly.filter((w) => w.weekday === weekdayOf(date));
}

export function PtPage() {
  const { client } = useServices();
  const sc = useScope();
  const wide = useIsWide();
  const { nameOf, list } = useStaffNames();
  const [anchor, setAnchor] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [trainerPick, setTrainerPick] = useState<string>("");
  const [openId, setOpenId] = useState<string | null>(null);
  /** A session opened from a list that is not the visible week (unmarked, leave): the week's query does not hold it. */
  const [extra, setExtra] = useState<PtSession | null>(null);
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  const [booking, setBooking] = useState(false);
  const [editAv, setEditAv] = useState<string | null>(null);
  const [hoursPick, setHoursPick] = useState("");
  const [dragId, setDragId] = useState<string | null>(null);

  const week = weekDates(anchor ?? sc.date);
  const day = selected && week.includes(selected) ? selected : week.includes(sc.date) ? sc.date : week[0]!;
  const sessionsQ = useQuery({ queryKey: ["pt", "sessions", ...sc.key, week[0]], queryFn: () => listPtSessions(client, { from: week[0]!, to: week[6]!, branchId: sc.branchId }).then((r) => r.sessions), retry: false });
  const availQ = useQuery({ queryKey: ["pt", "availability", ...sc.key], queryFn: () => listAvailability(client, { branchId: sc.branchId }).then((r) => r.availability), retry: false });

  const sessions = sessionsQ.data ?? [];
  const avail = availQ.data ?? [];
  const trainers = useMemo(() => {
    const ids = new Set<string>([...avail.map((a) => a.trainerId), ...sessions.map((s) => s.trainerId)]);
    return [...ids].sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  }, [avail, sessions, list]); // eslint-disable-line react-hooks/exhaustive-deps -- `list` is what resolves names, so re-sort when it arrives
  const shown = wide ? trainers : trainers.filter((t) => t === (trainerPick || trainers[0]));
  const live = sessions.filter((s) => s.status !== "rescheduled");
  const opened = sessions.find((s) => s.id === openId) ?? (extra && extra.id === openId ? extra : null);
  const openFrom = (s: PtSession) => { setPrefill(null); setExtra(s); setOpenId(s.id); };
  // One lever per action (D57): hours, moving a session, marking, handing over, booking.
  const canHours = sc.ctx.has("pt.availability");
  const canMove = sc.ctx.has("pt.reschedule");
  const canAdd = sc.ctx.has("pt.book");
  const myId = sc.ctx.me?.staff.id;
  // Columns come from availability and sessions, so someone with neither needs this way in to get their first hours.
  const noColumn = (list.length > 0 ? list.map((m) => m.id) : myId ? [myId] : []).filter((id) => !trainers.includes(id));
  const hoursFor = noColumn.includes(hoursPick) ? hoursPick : noColumn[0] ?? "";
  const windowsFor = (t: string) => avail.filter((a) => a.trainerId === t).flatMap((a) => windowsOn(a, day));
  const pending = sessionsQ.isPending || availQ.isPending;
  const failure = sessionsQ.error ?? availQ.error;

  const dayLive = live.filter((s) => s.date === day);
  const all = [...dayLive, ...shown.flatMap((t) => windowsFor(t).map((w) => ({ start: w.start, end: w.end })))];
  const first = Math.floor(Math.min(6 * 60, ...all.map((x) => toMinutes(x.start))) / 60) * 60;
  const last = Math.ceil(Math.max(21 * 60, ...all.map((x) => toMinutes(x.end))) / 60) * 60;
  const hours = Array.from({ length: (last - first) / 60 }, (_, i) => first + i * 60);
  const nowMin = minutesOfDay(sc.nowMs, sc.ctx.timeZone);

  const onDrop = (trainerId: string) => (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const s = sessions.find((x) => x.id === dragId);
    setDragId(null);
    if (!s) return;
    const top = e.currentTarget.getBoundingClientRect().top;
    const start = minutesFromOffset((Number.isFinite(e.clientY) ? e.clientY : top) - top, PX, first);
    const len = toMinutes(s.end) - toMinutes(s.start);
    setOpenId(s.id);
    setPrefill({ date: day, start: fromMinutes(start), end: fromMinutes(Math.min(1439, start + len)), trainerId });
  };

  return (
    <div className={c.page}>
      <div className={c.head}>
        <div><h1>PT calendar</h1><p>Trainer by trainer. Shaded hours are when each is free to take sessions.</p></div>
        <div className={c.tools}>
          {!areaAllows("staff", sc.ctx.has) ? <Button as={Link} to="/me/attendance">My attendance</Button> : null}
          <Button as={Link} to="/classes">Class calendar</Button>
          {canAdd ? <Button variant="primary" onClick={() => setBooking(true)}>Book a session</Button> : null}
        </div>
      </div>
      <div className={c.spread}>
        <div className={c.row}>
          <Button size="sm" aria-label="Previous week" onClick={() => setAnchor(addDays(week[0]!, -7))}>‹</Button>
          <Button size="sm" onClick={() => { setAnchor(null); setSelected(null); }}>This week</Button>
          <Button size="sm" aria-label="Next week" onClick={() => setAnchor(addDays(week[0]!, 7))}>›</Button>
          <strong>{formatDate(week[0]!, sc.ctx.locale, "short")} – {formatDate(week[6]!, sc.ctx.locale, "short")}</strong>
        </div>
        {!wide && trainers.length > 1 ? (
          <Field label="Trainer" htmlFor="pt-pick"><select id="pt-pick" className={c.input} value={trainerPick || trainers[0]} onChange={(e) => setTrainerPick(e.target.value)}>{trainers.map((t) => <option key={t} value={t}>{nameOf(t)}</option>)}</select></Field>
        ) : null}
      </div>
      {canHours && hoursFor ? (
        <div className={c.row}>
          <Field label="Set hours for" htmlFor="pt-hours"><select id="pt-hours" className={c.input} value={hoursFor} onChange={(e) => setHoursPick(e.target.value)}>{noColumn.map((t) => <option key={t} value={t}>{nameOf(t)}</option>)}</select></Field>
          <Button size="sm" onClick={() => setEditAv(hoursFor)}>Set hours</Button>
        </div>
      ) : null}
      <div className={k.dayPick} role="group" aria-label="Day of the week">
        {week.map((d) => <button key={d} type="button" aria-pressed={d === day} onClick={() => setSelected(d)}>{formatDate(d, sc.ctx.locale, "weekday")}{d === sc.date ? " •" : ""}</button>)}
      </div>
      <Card aria-label="PT calendar">
        {pending ? <div aria-busy="true" aria-label="Loading PT calendar" className={k.skGrid}><Skeleton width="30%" height={20} /><Skeleton width="80%" height={60} /></div> : failure ? <Failure error={failure} title="Could not load the PT calendar" onRetry={() => { void sessionsQ.refetch(); void availQ.refetch(); }} /> : (
          <>
            {live.length === 0 ? <EmptyState compact icon="clock" title="No PT sessions this week" description={canAdd ? "Book a session to see it here." : undefined} action={canAdd ? <Button onClick={() => setBooking(true)}>Book a session</Button> : undefined} /> : null}
            {trainers.length === 0 ? null : (
              <div className={k.cal} role="region" aria-label="Trainer columns" tabIndex={0}>
                <div className={k.calHead} style={{ ["--cols" as string]: shown.length, ["--col-min" as string]: "168px" }}>
                  <div className={k.gutter} />
                  {shown.map((t) => (
                    <div key={t} className={k.dayHead} role="columnheader"><span className={c.trunc} title={nameOf(t)}>{nameOf(t)}</span>
                      {canHours ? <Button size="sm" variant="plain" aria-label={`Edit availability for ${nameOf(t)}`} onClick={() => setEditAv(t)}>Hours</Button> : null}
                    </div>
                  ))}
                </div>
                <div className={k.calBody} style={{ ["--cols" as string]: shown.length, ["--col-min" as string]: "168px", height: (last - first) * PX }}>
                  <div className={k.gutter}>{hours.map((h) => <span key={h} className={k.hour} style={{ top: (h - first) * PX }}>{fromMinutes(h)}</span>)}</div>
                  {shown.map((t) => (
                    <div key={t} className={`${k.col} ${dragId ? k.dropHint : ""}`} data-trainer={t} role="group" aria-label={`${nameOf(t)} on ${day}`}
                      onDragOver={wide && canMove ? (e) => e.preventDefault() : undefined} onDrop={wide && canMove ? onDrop(t) : undefined}>
                      {hours.map((h) => <div key={h} className={k.line} style={{ top: (h - first) * PX }} />)}
                      {windowsFor(t).map((w, i) => <div key={i} className={k.shade} data-testid="available" title={`Available ${w.start}–${w.end}`} style={{ top: (toMinutes(w.start) - first) * PX, height: (toMinutes(w.end) - toMinutes(w.start)) * PX }} />)}
                      {day === sc.date ? <div className={k.now} style={{ top: (nowMin - first) * PX }} aria-hidden="true" /> : null}
                      {layoutLanes(dayLive.filter((s) => s.trainerId === t), (s) => s).map(({ item: s, lane, lanes }) => (
                        <SessionBlock key={s.id} s={s} lane={lane} lanes={lanes} first={first} draggable={wide && canMove && s.status === "booked"} onOpen={() => { setPrefill(null); setOpenId(s.id); }} onDrag={(on) => setDragId(on ? s.id : null)} />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {trainers.length === 0 && live.length > 0 ? <p className={`${c.note} ${c.pad}`}>No trainers to show.</p> : null}
          </>
        )}
      </Card>
      {sc.ctx.has("pt.complete") ? <UnmarkedSessions nameOf={nameOf} onOpen={openFrom} /> : null}
      {sc.ctx.has("pt.change_trainer") ? <LeaveImpact trainers={trainers} nameOf={nameOf} onOpen={openFrom} /> : null}
      <PtSessionDrawer session={opened} trainers={trainers} nameOf={nameOf} prefill={prefill} onClose={() => { setOpenId(null); setPrefill(null); }} />
      {booking ? <PtBookDrawer open onClose={() => setBooking(false)} date={day} trainers={trainers.length ? trainers : sc.ctx.me ? [sc.ctx.me.staff.id] : []} nameOf={nameOf} /> : null}
      {editAv ? <AvailabilityDrawer open onClose={() => setEditAv(null)} trainerId={editAv} trainerName={nameOf(editAv)} branchId={avail.find((a) => a.trainerId === editAv)?.branchId ?? sc.ctx.me?.branch?.id ?? ""} current={avail.find((a) => a.trainerId === editAv) ?? null} /> : null}
    </div>
  );
}

function SessionBlock({ s, lane, lanes, first, draggable, onOpen, onDrag }: { s: PtSession; lane: number; lanes: number; first: number; draggable: boolean; onOpen: () => void; onDrag: (on: boolean) => void }) {
  const tone = s.status === "completed" ? k.done : s.status === "booked" ? "" : k.off;
  const name = s.member?.name ?? "Member";
  return (
    <button type="button" className={`${k.block} ${tone}`} draggable={draggable} aria-label={`${s.start} ${name}, ${s.status.replace("_", " ")}`} onClick={onOpen}
      onDragStart={draggable ? (e) => { e.dataTransfer.setData("text/plain", s.id); e.dataTransfer.effectAllowed = "move"; onDrag(true); } : undefined} onDragEnd={draggable ? () => onDrag(false) : undefined}
      style={{ top: (toMinutes(s.start) - first) * PX, height: Math.max(24, (toMinutes(s.end) - toMinutes(s.start)) * PX - 2), left: `${(lane / lanes) * 100}%`, width: `calc(${100 / lanes}% - 2px)` }}>
      <b className={c.trunc}>{s.start} {name}</b>
      <span>{s.status === "booked" ? (s.sessionIndex ? `Session ${s.sessionIndex}` : "Booked") : s.status.replace("_", " ")}</span>
    </button>
  );
}

export { Badge };
